import { useCallback, useMemo, useState } from "react";
import type { CompilationTheme } from "@memento/types/compilation-theme";
import type { RenderConfig } from "@memento/types/render-config";
import {
  getCompositionDurationInSeconds,
  toFrames,
} from "@memento/library/render";

export type UsePlayerHelperType = ReturnType<typeof usePlayerHelper>;

type OverlayBehavior =
  // show the overlay
  | "show"
  // hide the overlay
  | "hide"
  // don't change the overlay state
  | "current";

export const usePlayerHelper = (
  editor: RenderConfig,
  theme: CompilationTheme
) => {
  // These states are managed by the controls below the Remotion Player,
  // but need to be accessed outside of the RemotionPlayerProvider

  // If these useState values are just numbers or booleans, setting
  // it to the same value when the player is playing will not trigger
  // a re-render. Setting it to an object will.
  const [frameToSeek, setFrameToSeek] = useState<{
    frame: number;
    pause?: boolean;
    overlay: OverlayBehavior;
  }>({ frame: 0, overlay: "current" });
  const [playPauseRequest, setPlayPauseRequest] = useState({ play: false });

  const [isRemotionPlayerLooping, setIsRemotionPlayerLooping] = useState(false);
  const [isRemotionPlayerPlaying, setIsRemotionPlayerPlaying] = useState(false);
  const [isRemotionPlayerFullscreen, setIsRemotionPlayerFullscreen] =
    useState(false);
  const [displayStartPlayerOverlay, setDisplayStartPlayerOverlay] =
    useState(true);

  const {
    videoFrame: { fps },
  } = editor;

  const durationInFrames = useMemo(() => {
    const durationInSeconds = getCompositionDurationInSeconds(editor, theme);
    const durationInFrames = toFrames(durationInSeconds, fps);

    return durationInFrames;
  }, [editor, theme, fps]);

  const toggleLoop = () => {
    setIsRemotionPlayerLooping(!isRemotionPlayerLooping);
  };

  // proxy setFrameToSeek so that we can set default values
  const setFrameToSeekProxy = useCallback(
    ({
      frame,
      pause,
      overlay = "hide",
    }: {
      frame: number;
      pause?: boolean;
      overlay?: OverlayBehavior;
    }) => {
      setFrameToSeek({ frame, pause, overlay });
    },
    [setFrameToSeek]
  );

  return {
    durationInFrames,
    setFrameToSeek: setFrameToSeekProxy,
    frameToSeek,
    playPauseRequest,
    setPlayPauseRequest,
    displayStartPlayerOverlay,
    setDisplayStartPlayerOverlay,
    isRemotionPlayerLooping,
    toggleLoop,
    isRemotionPlayerFullscreen,
    setIsRemotionPlayerFullscreen,
    setIsRemotionPlayerPlaying,
    isRemotionPlayerPlaying,
  };
};
